// useUndoRedo — generisk debounce:ad snapshot-historik (#5 i
// PRE_RELEASE_REVIEW_100, extraherad ur useMultiSheetWorkflow).
//
// Mekanik (verbatim från ursprunget):
//   • Varje state-ändring fångas efter 300 ms debounce (rapid-fire-
//     mutationer som drag → EN history-entry, inte 60/s).
//   • Capped till MAX_HISTORY entries (äldsta shiftas ut).
//   • undo/redo sätter state via isRestoringRef-guarden så restore-
//     writes inte själva pushas till historiken.
//   • Branch-trunkering: ny ändring efter undo kapar redo-grenen
//     (klassisk linjär historik, samma som de flesta editorer).

import { useState, useRef, useEffect, useCallback } from 'react';
import { MAX_HISTORY } from './internals';

export interface UndoRedoApi {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useUndoRedo<T>(
  state: T,
  setState: (snapshot: T) => void,
): UndoRedoApi {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const historyRef = useRef<T[]>([]);
  const historyIndexRef = useRef(-1);
  const isRestoringRef = useRef(false);

  useEffect(() => {
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const trimmed = historyRef.current.slice(0, historyIndexRef.current + 1);
      trimmed.push(state);
      if (trimmed.length > MAX_HISTORY) trimmed.shift();
      historyRef.current = trimmed;
      historyIndexRef.current = trimmed.length - 1;
      setCanUndo(trimmed.length > 1);
      setCanRedo(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [state]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    isRestoringRef.current = true;
    historyIndexRef.current--;
    const snapshot = historyRef.current[historyIndexRef.current];
    setState(snapshot);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);
    // setState är en React state-setter (stabil identitet) hos den enda
    // konsumenten; deps utelämnas medvetet för paritet med ursprunget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    isRestoringRef.current = true;
    historyIndexRef.current++;
    const snapshot = historyRef.current[historyIndexRef.current];
    setState(snapshot);
    setCanUndo(true);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { undo, redo, canUndo, canRedo };
}
